import { inject, Injectable, InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';

import { CaseTypeEditorEffects } from '../../../effects/case-type-editor.effects';
import type { CaseTypeEditorContext } from '../../../effects/case-type-editor.effects';
import { SaveProtocolCascadeEffects } from '../../../effects/save-protocol-cascade.effects';
import { CaseTypeEditorState } from '../../../state/case-type-editor.state';

/**
 * The editing session's identity, provided ONCE at page scope: the route's
 * case-type id for an existing case type, or `undefined` (create) for a new
 * one. Unlike the method-type editor's context, the case-type id is NOT remapped
 * into any child context, so a create session needs no `tmp-` placeholder.
 */
export const CASE_TYPE_EDITOR_CONTEXT = new InjectionToken<CaseTypeEditorContext>('CASE_TYPE_EDITOR_CONTEXT');

/**
 * Save-cascade consumer for the route host (`CaseTypeEditorPageComponent`).
 *
 * `CaseTypeEditorComponent` is a pure draft producer — its `save`/`cancel` emit
 * a one-shot `CaseTypeEditorState.outcome$` event and never persist. This facade
 * owns the route-side decision: on `saved` it triggers the case-type
 * consolidation (TODAY A STUB that completes immediately and persists nothing —
 * the persistence work package replaces its body; this wiring is final), and it
 * exposes the navigation intents (`saved$`, `cancelled$`) for the thin host.
 * Mirrors `MethodTypeEditorPageFacade`.
 */
@Injectable()
export class CaseTypeEditorPageFacade {
  private readonly context = inject<CaseTypeEditorContext>(CASE_TYPE_EDITOR_CONTEXT);

  constructor(
    private readonly editorEffects: CaseTypeEditorEffects,
    private readonly state: CaseTypeEditorState,
    private readonly cascadeEffects: SaveProtocolCascadeEffects
  ) {}

  /**
   * Loads the editor for the session's case type (the effects also seed the
   * shared staged-data and assigned-method-type ledgers) and wires the save
   * reaction: every `saved` outcome triggers the cascade. Built on
   * `state.effect` so the subscription's teardown follows the per-page state
   * lifecycle, and a retry after a failure re-triggers naturally.
   */
  public readonly initialize = this.state.effect<void>((trigger$) =>
    trigger$.pipe(
      tap(() => this.editorEffects.loadCaseType(this.context)),
      switchMap(() => this.state.outcome$),
      filter((outcome) => outcome.kind === 'saved'),
      tap(() => this.cascadeEffects.consolidateCaseType())
    )
  );

  /** Navigation intent: the case-type consolidation finished. */
  public readonly saved$: Observable<void> = this.cascadeEffects.cascadeCompleted$;

  /** Navigation intent: the user discarded the edit. */
  public readonly cancelled$: Observable<void> = this.state.outcome$.pipe(
    filter((outcome) => outcome.kind === 'cancelled'),
    map(() => undefined)
  );
}
